import { useRef, useState, useEffect } from 'react'
import { useProjectContext } from '../../context/ProjectContext'
import DirectionTool from './DirectionTool'
import LoopHeightTool from './LoopHeightTool'
import UndoRedoButtons from './UndoRedoButtons'
import './Toolbar.css'

const Toolbar = ({ leadingControls = null }) => {
  const {
    selectedTool,
    setSelectedTool,
    setSelectedStitchCell,
    previewMode,
  } = useProjectContext()

  const toolButtons = [
    { key: 'paint', label: '🖌️ Paint', title: 'Paint Tool' },
    { key: 'place-stitch', label: '🪡 Stitch Placement', title: 'Place Stitch Tool' },
    { key: 'select-stitch', label: '🖱️ Select', title: 'Select / Move Stitch Tool' },
    { key: 'erase', label: '🧽 Erase', title: 'Erase Tool' },
    { key: 'hand', label: '✋ Hand', title: 'Grab Hand Tool' },
    { key: 'direction', label: '➡️ Direction', title: 'Direction Tool' },
    { key: 'loopHeight', label: '📏 Height', title: 'Loop Height Tool' },
  ]

  const directionBtnRef = useRef(null)
  const loopHeightBtnRef = useRef(null)
  const subpanelRef = useRef(null)

  const [activeSubpanel, setActiveSubpanel] = useState(null)
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 })

  const PANEL_WIDTH = 180
  const SCREEN_PADDING = 12

  const openSubpanel = (type, ref) => {
    if (activeSubpanel === type) {
      setActiveSubpanel(null)
      return
    }

    if (!ref.current) return

    const rect = ref.current.getBoundingClientRect()

    const buttonCenter = rect.left + rect.width / 2
    let nextLeft = buttonCenter - PANEL_WIDTH / 2
    const nextTop = rect.bottom + 6

    const maxLeft = window.innerWidth - PANEL_WIDTH - SCREEN_PADDING

    nextLeft = Math.max(SCREEN_PADDING, nextLeft)
    nextLeft = Math.min(maxLeft, nextLeft)

    setPanelPosition({
      top: nextTop,
      left: nextLeft,
    })

    setActiveSubpanel(type)
  }

  useEffect(() => {
    if (!activeSubpanel) return

    const handleClickOutside = (event) => {
      const clickedInsidePanel = subpanelRef.current?.contains(event.target)
      const clickedDirectionBtn = directionBtnRef.current?.contains(event.target)
      const clickedLoopBtn = loopHeightBtnRef.current?.contains(event.target)

      if (!clickedInsidePanel && !clickedDirectionBtn && !clickedLoopBtn) {
        setActiveSubpanel(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [activeSubpanel])

  useEffect(() => {
    if (!activeSubpanel) return

    const handleResize = () => {
      if (activeSubpanel === 'direction' && directionBtnRef.current) {
        const rect = directionBtnRef.current.getBoundingClientRect()

        const buttonCenter = rect.left + rect.width / 2
        let nextLeft = buttonCenter - PANEL_WIDTH / 2
        const nextTop = rect.bottom + 6

        const maxLeft = window.innerWidth - PANEL_WIDTH - SCREEN_PADDING

        nextLeft = Math.max(SCREEN_PADDING, nextLeft)
        nextLeft = Math.min(maxLeft, nextLeft)

        setPanelPosition({ top: nextTop, left: nextLeft })
      }

      if (activeSubpanel === 'loopHeight' && loopHeightBtnRef.current) {
        const rect = loopHeightBtnRef.current.getBoundingClientRect()

        const buttonCenter = rect.left + rect.width / 2
        let nextLeft = buttonCenter - PANEL_WIDTH / 2
        const nextTop = rect.bottom + 6

        const maxLeft = window.innerWidth - PANEL_WIDTH - SCREEN_PADDING

        nextLeft = Math.max(SCREEN_PADDING, nextLeft)
        nextLeft = Math.min(maxLeft, nextLeft)

        setPanelPosition({ top: nextTop, left: nextLeft })
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [activeSubpanel])

  return (
    <div className="toolbar">
      <div className="toolbar-main">
        {leadingControls && (
          <div className="toolbar-inline-group">
            {leadingControls}
          </div>
        )}

        <div className="toolbar-section">
          <div className="toolbar-label">Tools</div>

          <div className="tool-buttons">
            {toolButtons.map((tool) => {
              const buttonRef =
                tool.key === 'direction'
                  ? directionBtnRef
                  : tool.key === 'loopHeight'
                    ? loopHeightBtnRef
                    : null

              const isActive =
                tool.key === 'direction'
                  ? activeSubpanel === 'direction'
                  : tool.key === 'loopHeight'
                    ? activeSubpanel === 'loopHeight'
                    : selectedTool === tool.key

              const handleClick = () => {
                if (tool.key === 'direction') {
                  openSubpanel('direction', directionBtnRef)
                  return
                }

                if (tool.key === 'loopHeight') {
                  openSubpanel('loopHeight', loopHeightBtnRef)
                  return
                }

                setActiveSubpanel(null)
                setSelectedTool(tool.key)

                if (tool.key !== 'select-stitch') {
                  setSelectedStitchCell(null)
                }
              }

              return (
                <button
                  key={tool.key}
                  ref={buttonRef}
                  type="button"
                  className={isActive ? 'active' : ''}
                  onClick={handleClick}
                  title={tool.title}
                  disabled={previewMode}
                >
                  {tool.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="toolbar-section toolbar-history">
          <div className="toolbar-label">History</div>
          <UndoRedoButtons />
        </div>
      </div>

      {!previewMode && activeSubpanel === 'direction' && (
        <div
          ref={subpanelRef}
          className="toolbar-subpanel anchored-subpanel direction-subpanel"
          style={{
            position: 'fixed',
            top: panelPosition.top,
            left: panelPosition.left,
          }}
        >
          <DirectionTool onClose={() => setActiveSubpanel(null)} />
        </div>
      )}

      {!previewMode && activeSubpanel === 'loopHeight' && (
        <div
          ref={subpanelRef}
          className="toolbar-subpanel anchored-subpanel height-subpanel"
          style={{
            position: 'fixed',
            top: panelPosition.top,
            left: panelPosition.left,
          }}
        >
          <LoopHeightTool onClose={() => setActiveSubpanel(null)} />
        </div>
      )}
    </div>
  )
}

export default Toolbar